/**
 * A reusable pagination component, that the user can interact with to navigate
 * to different pages of results in different tables
 */
import { Icon, IconButton, P, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { noop } from 'lodash';

type PaginationProps = {
  count: number;
  page: number;
  rowsPerPage?: number;
  onChangePage: (page: number) => void;
};

function Pagination({
  rowsPerPage = 15,
  count,
  onChangePage,
  page,
}: PaginationProps) {
  /**
   * Decrements the page number to the previous page in the list. Calls the
   * onChangePage function from props and reads the page prop, and subtracts
   * one to the page parameter in the url.
   */
  const handleBackButtonClick = () => {
    const nextPage = page - 1;

    onChangePage(nextPage);
  };

  /**
   * Increments the page number to the next page in the list. Calls the
   * onChangePage function from props an reads the page number from props and
   * adds one to the page parameter in the url.
   */
  const handleNextButtonClick = () => {
    const nextPage = page + 1;

    onChangePage(nextPage);
  };

  const lastPage = Math.ceil(count / rowsPerPage);
  const itemStart = rowsPerPage * (page - 1) + 1;
  const itemEnd = rowsPerPage * page;

  return !count ? null : (
    <Stack
      direction="row"
      gap="md"
      align="center"
      className="sticky bottom-0 shrink-0 justify-end pr-[30px] pt-3 pb-[10px] bg-neutral-0 border border-l-0 border-neutral-300"
    >
      <P size="xsm" color="primary">
        {count === 1 ? '' : `${itemStart} - `}
        {count < itemEnd ? count : itemEnd} of {count}
      </P>

      <Stack width="fit" direction="row" gap="xs">
        <IconButton
          icon={<Icon.ChevronLeft />}
          label="Prevous Page"
          data-testid="prevPageButton"
          color="secondary"
          onClick={page !== 1 ? handleBackButtonClick : noop}
          disabled={page === 1}
        />

        <IconButton
          icon={<Icon.ChevronRight />}
          label="Next Page"
          data-testid="nextPageButton"
          color="secondary"
          onClick={page !== lastPage ? handleNextButtonClick : noop}
          disabled={page === lastPage}
        />
      </Stack>
    </Stack>
  );
}

Pagination.defaultProps = {
  rowsPerPage: 15,
};

export default Pagination;
